// Utilidades para normalizar direcciones BOE de cara al usuario y a SEO.
// El BOE publica direcciones en MAYÚSCULAS con abreviaturas crudas y espacios
// múltiples, p.ej. "CL ZURBANO                   71   2  03 04".
// Esto perjudica el CTR en SERP y la legibilidad. Aquí lo convertimos a un
// formato humano: "Calle Zurbano 71, 2º · 3-4".

const ABBR = {
  CL: "Calle",
  "C/": "Calle",
  "C\\.": "Calle",
  AV: "Avenida",
  AVDA: "Avenida",
  "AV\\.": "Avenida",
  PL: "Plaza",
  "PL\\.": "Plaza",
  PZA: "Plaza",
  PZ: "Plaza",
  PSO: "Paseo",
  PS: "Paseo",
  CR: "Carretera",
  CTRA: "Carretera",
  "CTRA\\.": "Carretera",
  TR: "Travesía",
  TRV: "Travesía",
  GL: "Glorieta",
  GTA: "Glorieta",
  RD: "Ronda",
  RDA: "Ronda",
  CM: "Camino",
  UR: "Urbanización",
  URB: "Urbanización",
  BO: "Barrio",
  BARRIO: "Barrio",
  POL: "Polígono",
  PG: "Polígono",
  EDIF: "Edificio",
  EDF: "Edificio",
};

// "ZURBANO" → "Zurbano", "DE LA CRUZ" → "de la Cruz", "JOSÉ" → "José".
// Preserva acentos y ñ.
function toTitleCase(str) {
  const lowerWords = new Set(["de", "del", "la", "las", "los", "el", "y", "en", "al"]);
  return str
    .toLowerCase()
    .split(/(\s+)/) // conserva los separadores
    .map((token, i) => {
      if (/^\s+$/.test(token)) return token;
      if (i > 0 && lowerWords.has(token)) return token;
      return token.charAt(0).toUpperCase() + token.slice(1);
    })
    .join("");
}

/**
 * Limpia una dirección cruda del BOE.
 * Ejemplos:
 *   "CL ZURBANO                   71   2  03 04" → "Calle Zurbano 71, 2º 3-4"
 *   "AV DE LA CONSTITUCION 12 P02 IZQ"          → "Avenida de la Constitución 12, P02 Izq"
 *   "PL MAYOR 3 BJ A"                            → "Plaza Mayor 3, Bj A"
 */
export function normalizeBoeAddress(raw) {
  if (!raw || typeof raw !== "string") return "";
  // Colapsar espacios múltiples y trim
  let s = raw.replace(/\s+/g, " ").trim();
  if (!s) return "";

  // Capitalizar primero (mientras todavía está separado por espacios simples)
  s = toTitleCase(s);

  // Expandir abreviatura al inicio: "Cl ..." → "Calle ..."
  // Después de title-case, "CL" se convierte en "Cl", "AVDA" → "Avda", etc.
  const tokens = s.split(" ");
  if (tokens.length > 0) {
    const head = tokens[0].toUpperCase();
    const headDot = head.replace(/[.]/g, "\\.");
    const expanded = ABBR[head] || ABBR[headDot];
    if (expanded) {
      tokens[0] = expanded;
      s = tokens.join(" ");
    }
  }

  // Insertar una coma entre el número (calle nº) y los segmentos posteriores
  // (planta/puerta). Heurística: tras la primera secuencia "<calle> <numero>",
  // si quedan más tokens, insertamos coma.
  // Detecta el primer número entero del bloque.
  const numMatch = s.match(/^(.+?\s\d+[A-Za-z]?)\b(\s+.+)$/);
  if (numMatch) {
    s = numMatch[1] + "," + numMatch[2].replace(/\s+/g, " ");
  }

  // Pequeñas mejoras de display tras la coma: "2 03 04" → "2º · 3-4"
  // Detectamos sufijo numérico tipo "2 03 04" o "2 03" después de coma.
  s = s.replace(
    /,\s*(\d{1,2})\s+(\d{2})\s+(\d{2})\b/,
    (_, planta, esc, puerta) => `, ${parseInt(planta, 10)}º ${parseInt(esc, 10)}-${parseInt(puerta, 10)}`,
  );
  s = s.replace(
    /,\s*(\d{1,2})\s+(\d{2})\b/,
    (_, planta, puerta) => `, ${parseInt(planta, 10)}º · ${parseInt(puerta, 10)}`,
  );

  return s;
}

/**
 * Capitaliza una localidad (ej. "MADRID" → "Madrid", "VITORIA-GASTEIZ" → "Vitoria-Gasteiz").
 */
export function normalizeLocalidad(raw) {
  if (!raw || typeof raw !== "string") return "";
  return raw
    .toLowerCase()
    .split(/([-\s]+)/)
    .map((token) => (/^[-\s]+$/.test(token) ? token : token.charAt(0).toUpperCase() + token.slice(1)))
    .join("");
}
